import React, { ComponentType, FunctionComponent } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { Form } from '@erecruit/ui-kit';
import { Intl } from '@erecruit/core';
import { PositionTemplateFieldType } from '@erecruit/types';

import {
  AddNewPositionWizardFields,
  WizardContext,
  PositionField,
  PositionTemplate,
} from './types';
import {
  datepickerFields,
  singleSelectFields,
  multiSelectFields,
} from '../RequestPosition/state/helpers';

const { FormattedMessage } = Intl;

export const generateFieldObjects = (
  fields: { [key: string]: any } | undefined, // eslint-disable-line @typescript-eslint/no-explicit-any
) =>
  // TODO expand with error if necessary
  fields
    ? Object.keys(fields).reduce(
        (acc, key) => ({ ...acc, [key]: { value: fields[key] } }),
        {},
      )
    : {};

export const hasValidationErrors = (
  validations: { [field: string]: any } | undefined, // eslint-disable-line @typescript-eslint/no-explicit-any
) =>
  validations &&
  Object.keys(validations).find(field => validations[field] != null);

interface FormCallbacks {
  onError?: (error: unknown) => void;
  onValidFieldsSubmit?: (values: unknown) => void;
}

export const withInitialFormValues = (
  Component: ComponentType,
  formFieldsValues: AddNewPositionWizardFields,
  wrappedComponentRef: any, // eslint-disable-line @typescript-eslint/no-explicit-any
): FunctionComponent<FormCallbacks> => (props: FormCallbacks) => {
  const { onError, onValidFieldsSubmit, ...componentProps } = props;

  return (
    <Form
      wrappedComponentRef={wrappedComponentRef}
      onError={onError}
      onValidFieldsSubmit={onValidFieldsSubmit}
    >
      <WizardContext.Provider value={formFieldsValues}>
        <Component {...componentProps} />
      </WizardContext.Provider>
    </Form>
  );
};

//transform first letter to lowercase
export const getFieldSlug = (field: PositionField): string => {
  const fieldName = PositionTemplateFieldType[field.fieldType];
  return fieldName.charAt(0).toLowerCase() + fieldName.slice(1);
};

const isDateField = (field: PositionField): boolean => {
  return datepickerFields.indexOf(field.fieldType) !== -1;
};

const isNumberField = (field: PositionField): boolean => {
  return singleSelectFields.indexOf(field.fieldType) !== -1;
};

const isNumberArrayField = (field: PositionField): boolean => {
  return multiSelectFields.indexOf(field.fieldType) !== -1;
};

/*
  HERE BE DRAGONS
  
  this interface is ugly because we need to cast proper types
  for AddNewPositionWizardFields when setting initial values and the fields are 
  fetched from the backend and are dynamic.

  Benefit: we can define AddNewPositionWizardFields fields proper types and without
  this they would all be type: any
*/
interface PositionFieldValues {
  booleans: {
    location: { workFromHome: boolean };
  };
  dates: {
    schedule: {
      startTime: moment.Moment;
      endTime: moment.Moment;
      estimatedStartDate: moment.Moment;
      estimatedEndDate: moment.Moment;
    };
  };
  numbers: {
    general: {
      numberOfPositions: number;
      positionType: number;
    };
    location: {
      worksiteAddress: number;
    };
  };
  numberArrays: {
    location: {
      facilityDepartments: number[];
    };
  };
}

export const getInitialValuesFromTemplate = ({ fields }: PositionTemplate) => {
  const values: AddNewPositionWizardFields = {
    general: {},
    location: {},
    schedule: {},
    billing: {},
    additionalInfo: {},
    review: {},
  };

  const groupedFields = _.groupBy(fields, 'section');

  groupedFields.general.forEach(field => {
    if (!field.value) {
      return false;
    }
    const fieldSlug = getFieldSlug(
      field,
    ) as keyof AddNewPositionWizardFields['general'];

    if (isNumberField(field)) {
      values.general[
        fieldSlug as keyof PositionFieldValues['numbers']['general']
      ] = field.value as number;
    } else {
      // eslint-disable-next-line
      values.general[fieldSlug] = field.value as any;
    }
  });
  groupedFields.location.forEach(field => {
    if (!field.value) {
      return false;
    }
    const fieldSlug = getFieldSlug(
      field,
    ) as keyof AddNewPositionWizardFields['location'];
    if (isNumberArrayField(field)) {
      values.location[
        fieldSlug as keyof PositionFieldValues['numberArrays']['location']
      ] = field.value as number[];
    } else {
      // eslint-disable-next-line
      values.location[fieldSlug] = field.value as any;
    }
  });
  groupedFields.schedule.forEach(field => {
    if (!field.value) {
      return false;
    }
    const fieldSlug = getFieldSlug(
      field,
    ) as keyof AddNewPositionWizardFields['schedule'];

    if (isDateField(field)) {
      values.schedule[
        fieldSlug as keyof PositionFieldValues['dates']['schedule']
      ] = moment(field.value as string);
    } else {
      // eslint-disable-next-line
      values.schedule[fieldSlug] = field.value as any;
    }
  });
  groupedFields.review.forEach(field => {
    if (!field.value) {
      return false;
    }
    const fieldSlug = getFieldSlug(
      field,
    ) as keyof AddNewPositionWizardFields['review'];
    // eslint-disable-next-line
    values.review[fieldSlug] = field.value as any;
  });

  return values;
};

export const generateFieldOptions = (field: PositionField, value: unknown) => {
  const initialValue = value;

  const options = {
    initialValue,
    rules: [
      {
        required: field.settings && field.settings.required,
        message: (
          <FormattedMessage id={messageIds.vms.errors.form.requiredField} />
        ),
      },
    ],
  };

  return options;
};
